import { Receipt as ReceiptIcon, ExternalLink, Download } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'motion/react'
import { useState } from 'react'
import InlineFeedback from './InlineFeedback'
import { DataRow, AnimatedChevron } from '@tendbot/ui'
import type { Receipt } from '../../../shared/types/chat'
import type { FeedbackType } from '../types'
import { expandCollapse } from '@tendbot/ui'

interface Props {
  receipt: Receipt
}

export default function ReceiptCard({ receipt }: Props) {
  const { t } = useTranslation('chat')
  const { t: tc } = useTranslation('common')
  const [showItems, setShowItems] = useState<boolean>(false)

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="glass-card rounded-xl overflow-hidden border-l-4 border-gold bg-gold/5"
    >
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ReceiptIcon size={14} className="text-gold" />
            <span className="text-xs font-semibold uppercase tracking-wider text-gold">
              {receipt.label || t('receipt.defaultLabel')}
            </span>
          </div>
          {receipt.status && (
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${
              receipt.status === 'paid'
                ? 'border border-emerald bg-emerald/10 text-emerald'
                : receipt.status === 'pending'
                  ? 'border border-amber bg-amber/10 text-amber'
                  : 'border border-pearl/20 bg-pearl/5 text-pearl/60'
            }`}>
              {receipt.status}
            </span>
          )}
        </div>

        {/* Merchant / Title */}
        <h4 className="text-ivory font-medium text-sm mb-0.5">{receipt.merchant}</h4>
        {receipt.subtitle && (
          <p className="text-pearl/50 text-xs mb-3">{receipt.subtitle}</p>
        )}

        {/* Key details */}
        <div className="space-y-1.5 mb-3">
          {receipt.details?.map((detail, i) => (
            <DataRow key={i} label={detail.label} value={detail.value} />
          ))}
        </div>

        {/* Line items -- expandable */}
        {receipt.lineItems && receipt.lineItems.length > 0 && (
          <div className="mb-3">
            <button
              onClick={() => setShowItems(!showItems)}
              className="flex items-center gap-1 text-xs text-gold/70 hover:text-gold transition-colors"
            >
              <AnimatedChevron isOpen={showItems} size={12} className="text-gold/70" />
              {showItems ? t('receipt.hideItems') : t('receipt.showItems')} {t('receipt.itemsCount', { count: receipt.lineItems.length })}
            </button>

            <AnimatePresence>
              {showItems && (
                <motion.div
                  {...expandCollapse}
                  className="overflow-hidden"
                >
                  <div className="mt-2 space-y-1">
                    {receipt.lineItems.map((item, i) => (
                      <div key={i} className="flex justify-between text-xs p-2 rounded-lg bg-charcoal/50">
                        <span className="text-pearl/70">
                          {item.qty && <span className="text-pearl/60">{item.qty}× </span>}
                          {item.name}
                        </span>
                        <span className="text-pearl font-medium">{item.amount}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Totals */}
        {receipt.totals && (
          <div className="pt-3 border-t border-white/10 space-y-1.5">
            {receipt.totals.subtotal && (
              <DataRow label={t('receipt.subtotal')} value={receipt.totals.subtotal} />
            )}
            {receipt.totals.tax && (
              <DataRow label={t('receipt.tax')} value={receipt.totals.tax} />
            )}
            {receipt.totals.discount && (
              <DataRow label={t('receipt.discount')} value={<>−{receipt.totals.discount}</>} valueClassName="text-emerald" />
            )}
            <div className="flex justify-between text-sm pt-1">
              <span className="text-ivory font-medium">{t('receipt.total')}</span>
              <span className="text-gold font-semibold">{receipt.totals.total}</span>
            </div>
            {receipt.totals.method && (
              <DataRow label={t('receipt.paidWith')} value={receipt.totals.method} valueClassName="text-pearl/50" />
            )}
          </div>
        )}

        {/* Assistant's note */}
        {receipt.note && (
          <div className="p-2.5 rounded-lg bg-gold/5 border border-gold/10 mt-3">
            <p className="text-xs text-pearl/70 italic">"{receipt.note}"</p>
          </div>
        )}

        {/* Actions */}
        {(receipt.receiptUrl || receipt.downloadUrl) && (
          <div className="flex gap-2 mt-3">
            {receipt.receiptUrl && (
              <a
                href={receipt.receiptUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-2 px-4 rounded-lg border border-white/10 text-pearl/70 text-sm font-medium hover:border-gold/30 hover:text-ivory transition-colors flex items-center justify-center gap-2"
              >
                <ExternalLink size={14} /> {t('receipt.viewReceipt')}
              </a>
            )}
            {receipt.downloadUrl && (
              <a
                href={receipt.downloadUrl}
                className="py-2 px-3 rounded-lg border border-white/10 text-pearl/70 text-sm font-medium hover:border-gold/30 hover:text-ivory transition-colors flex items-center justify-center"
              >
                <Download size={14} />
              </a>
            )}
          </div>
        )}

        {/* Feedback */}
        <div className="flex items-center justify-end gap-1 mt-3 pt-2 border-t border-white/5">
          <span className="text-xs text-pearl/50 mr-1">{tc('feedback.wasHelpful')}</span>
          <InlineFeedback
            id={`receipt-${receipt.id}`}
            onFeedback={(id: string, fb: FeedbackType) => console.log('Receipt feedback:', id, fb)}
          />
        </div>
      </div>
    </motion.div>
  )
}
